const databaseName = "kitae-transfer";
const storeName = "preferences";
const directoryKey = "last-directory";

function requestResult<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error("IndexedDB indisponible"));
  });
}

function transactionDone(transaction: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    transaction.oncomplete = () => resolve();
    transaction.onerror = () => reject(transaction.error ?? new Error("Écriture IndexedDB impossible"));
    transaction.onabort = () => reject(transaction.error ?? new Error("Écriture IndexedDB annulée"));
  });
}

export class DirectoryMemory {
  #memory?: FileSystemDirectoryHandle;

  async load(): Promise<FileSystemDirectoryHandle | undefined> {
    if (this.#memory) return this.#memory;
    let database: IDBDatabase | undefined;
    try {
      database = await this.#open();
      const transaction = database.transaction(storeName, "readonly");
      const handle = await requestResult(transaction.objectStore(storeName).get(directoryKey)) as FileSystemDirectoryHandle | undefined;
      this.#memory = handle;
      return handle;
    } catch {
      return undefined;
    } finally {
      database?.close();
    }
  }

  async save(handle: FileSystemDirectoryHandle): Promise<void> {
    this.#memory = handle;
    let database: IDBDatabase | undefined;
    try {
      database = await this.#open();
      const transaction = database.transaction(storeName, "readwrite");
      transaction.objectStore(storeName).put(handle, directoryKey);
      await transactionDone(transaction);
    } catch {
      // Some private browsing modes cannot clone handles. Session memory remains available.
    } finally {
      database?.close();
    }
  }

  async forget(): Promise<void> {
    this.#memory = undefined;
    let database: IDBDatabase | undefined;
    try {
      database = await this.#open();
      const transaction = database.transaction(storeName, "readwrite");
      transaction.objectStore(storeName).delete(directoryKey);
      await transactionDone(transaction);
    } catch {
      // Forgetting the in-memory handle is sufficient when persistent storage is unavailable.
    } finally {
      database?.close();
    }
  }

  async #open(): Promise<IDBDatabase> {
    if (!globalThis.indexedDB) throw new Error("IndexedDB indisponible");
    const request = globalThis.indexedDB.open(databaseName, 1);
    request.onupgradeneeded = () => {
      if (!request.result.objectStoreNames.contains(storeName)) request.result.createObjectStore(storeName);
    };
    return requestResult(request);
  }
}

export async function ensureReadWritePermission(handle: FileSystemDirectoryHandle): Promise<boolean> {
  try {
    if (!handle.queryPermission || !handle.requestPermission) return true;
    const current = await handle.queryPermission({ mode: "readwrite" });
    if (current === "granted") return true;
    return await handle.requestPermission({ mode: "readwrite" }) === "granted";
  } catch {
    return false;
  }
}
